<template>
  <div class="vue-image-crop-upload" v-show="value">
    <div class="vicp-wrap">
      <div class="vicp-close" @click="off">
        <i class="vicp-icon4" />
      </div>

      <div class="vicp-step1" v-show="step == 1">
        <div
          class="vicp-drop-area"
          @dragleave="preventDefault"
          @dragover="preventDefault"
          @dragenter="preventDefault"
          @click="handleClick"
          @drop="handleChange"
        >
          <b-avatar size="60" v-show="loading != 1">
            <feather-icon icon="UploadIcon" size="30" />
          </b-avatar>

          <span class="vicp-hint" v-show="loading !== 1">{{ lang.hint }}</span>
          <span class="vicp-no-supported-hint" v-show="!isSupported">
            {{ lang.noSupported }}
          </span>
          <input
            type="file"
            accept="image/*"
            v-show="false"
            v-if="step == 1"
            @change="handleChange"
            ref="fileinput"
          />
        </div>
        <div class="vicp-error mt-2" v-if="hasError">
          <b-avatar variant="danger" size="60">
            <feather-icon icon="XIcon" size="30" />
          </b-avatar>
          <p class="mt-1 mb-0">{{ errorMsg }}</p>
        </div>
        <div class="vicp-operate">
          <b-button variant="outline-danger" @click="off">
            {{ lang.btn.off }}
          </b-button>
        </div>
      </div>

      <div class="vicp-step2" v-if="step == 2">
        <div class="d-flex align-center justify-center">
          <div
            class="vicp-crop-left d-flex flex-column justify-content-between"
          >
            <div class="vicp-img-container" @wheel.prevent="handleMouseWheel">
              <img
                :src="sourceImgUrl"
                :style="sourceImgStyle"
                class="vicp-img"
                draggable="false"
                @drag="preventDefault"
                @dragstart="preventDefault"
                @dragend="preventDefault"
                @dragleave="preventDefault"
                @dragover="preventDefault"
                @dragenter="preventDefault"
                @drop="preventDefault"
                @touchstart="imgStartMove"
                @touchmove="imgMove"
                @touchend="createImg"
                @touchcancel="createImg"
                @mousedown="imgStartMove"
                @mousemove="imgMove"
                @mouseup="createImg"
                @mouseout="createImg"
                ref="img"
              />
              <div
                class="vicp-img-shade vicp-img-shade-1"
                :style="sourceImgShadeStyle"
              />
              <div
                class="vicp-img-shade vicp-img-shade-2"
                :style="sourceImgShadeStyle"
              />
            </div>

            <div class="vicp-range mt-2">
              <i
                @mousedown="startZoomSub"
                @mouseout="endZoomSub"
                @mouseup="endZoomSub"
                class="vicp-icon5"
              />

              <input
                type="range"
                :value="scale.range"
                step="1"
                min="0"
                max="100"
                @mousemove="zoomChange"
              />

              <i
                @mousedown="startZoomAdd"
                @mouseout="endZoomAdd"
                @mouseup="endZoomAdd"
                class="vicp-icon6"
              />
            </div>
          </div>
          <div class="flex-grow-1 vicp-crop-right">
            <div class="d-flex flex-column align-center justify-content-end">
              <div
                class="d-flex flex-column justify-content-between vicp-description"
              >
                <div class="vicp-description-area ml-3">
                  {{ $t('crop') }}
                </div>

                <div class="d-flex align-center justify-content-end">
                  <b-button variant="outline-secondary" @click="setStep(1)">
                    {{ lang.btn.back }}
                  </b-button>

                  <b-button
                    variant="primary"
                    class="ml-1"
                    @click="prepareUpload"
                  >
                    {{ lang.btn.save }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="vicp-step3" v-if="step == 3">
        <div class="vicp-upload d-flex align-center justify-content-center">
          <div
            class="d-flex align-center justify-content-center flex-column"
            v-if="loading === 1"
          >
            <p class="mt-0 mb-1">{{ lang.loading }}</p>

            <div class="vicp-progress-wrap" v-if="loading !== 2">
              <span class="vicp-progress" :style="progressStyle" />
            </div>
          </div>
          <div class="vicp-error" v-if="hasError">
            <b-avatar variant="danger" size="60">
              <feather-icon icon="XIcon" size="30" />
            </b-avatar>
            <p class="mt-1 mb-0">{{ errorMsg }}</p>
          </div>
          <div class="vicp-success" v-if="loading === 2">
            <b-avatar variant="success" size="60">
              <feather-icon icon="CheckIcon" size="30" />
            </b-avatar>
            <p class="mt-1 mb-0">{{ lang.success }}</p>
          </div>
        </div>
        <div class="vicp-operate">
          <b-button variant="outline-secondary" @click="setStep(2)">
            {{ lang.btn.back }}
          </b-button>

          <b-button variant="primary" class="ml-1" @click="off">
            {{ lang.btn.close }}
          </b-button>
        </div>
      </div>
      <canvas v-show="false" :width="width" :height="height" ref="canvas" />
    </div>
  </div>
</template>

<script>
'use strict'
import language from './utils/language.js'
import mimes from './utils/mimes.js'
import data2blob from './utils/data2blob.js'
import effectRipple from './utils/effectRipple.js'

import { BAvatar, BButton } from 'bootstrap-vue'

export default {
  components: {
    BAvatar,
    BButton,
  },
  props: {
    field: {
      type: String,
      default: 'avatar',
    },
    ki: {
      type: String,
      default: '0',
    },
    // eslint-disable-next-line vue/require-prop-types
    value: {
      default: true,
    },
    url: {
      type: String,
      default: '',
    },
    params: {
      type: Object,
      default: null,
    },
    // Add custom headers
    headers: {
      type: Object,
      default: null,
    },
    width: {
      type: Number,
      default: 200,
    },
    height: {
      type: Number,
      default: 200,
    },
    noCircle: {
      type: Boolean,
      default: false,
    },
    noSquare: {
      type: Boolean,
      default: false,
    },
    maxSize: {
      type: Number,
      default: 10240,
    },
    langType: {
      type: String,
      default: 'zh',
    },
    langExt: {
      type: Object,
      default: null,
    },
    imgFormat: {
      type: String,
      default: 'png',
    },
    imgBgc: {
      type: String,
      default: '#fff',
    },
    withCredentials: {
      type: Boolean,
      default: false,
    },
    method: {
      type: String,
      default: 'POST',
    },
    initialImgUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    let that = this,
      { imgFormat, langType, langExt, width, height } = that,
      isSupported = true,
      allowImgFormat = ['jpg', 'png'],
      tempImgFormat =
        allowImgFormat.indexOf(imgFormat) === -1 ? 'jpg' : imgFormat,
      lang = language[langType] ? language[langType] : language['en'],
      mime = mimes[tempImgFormat]
    that.imgFormat = tempImgFormat

    if (langExt) {
      Object.assign(lang, langExt)
    }
    if (typeof FormData != 'function') {
      isSupported = false
    }
    return {
      mime,
      lang,
      isSupported,
      isSupportTouch: document.hasOwnProperty('ontouchstart'),
      step: 1,
      loading: 0,
      progress: 0,
      hasError: false,
      errorMsg: '',
      ratio: width / height,
      sourceImg: null,
      sourceImgUrl: this.initialImgUrl,
      createImgUrl: this.initialImgUrl,
      sourceImgMouseDown: {
        on: false,
        mX: 0,
        mY: 0,
        x: 0,
        y: 0,
      },
      previewContainer: {
        width: 100,
        height: 100,
      },
      sourceImgContainer: {
        // sic
        width: 396,
        height: 304,
      },
      scale: {
        zoomAddOn: false,
        zoomSubOn: false,
        range: 1,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        maxWidth: 0,
        maxHeight: 0,
        minWidth: 0,
        minHeight: 0,
        naturalWidth: 0,
        naturalHeight: 0,
      },
    }
  },
  computed: {
    progressStyle() {
      let { progress } = this
      return {
        width: progress + '%',
      }
    },
    sourceImgStyle() {
      let { scale, sourceImgMasking } = this,
        top = scale.y + sourceImgMasking.y + 'px',
        left = scale.x + sourceImgMasking.x + 'px'
      return {
        top,
        left,
        width: scale.width + 'px',
        height: scale.height + 'px',
      }
    },
    sourceImgMasking() {
      let { width, height, ratio, sourceImgContainer } = this,
        sic = sourceImgContainer,
        sicRatio = sic.width / sic.height,
        x = 0,
        y = 0,
        w = sic.width,
        h = sic.height,
        scale = 1
      if (ratio < sicRatio) {
        scale = sic.height / height
        w = sic.height * ratio
        x = (sic.width - w) / 2
      }
      if (ratio > sicRatio) {
        scale = sic.width / width
        h = sic.width / ratio
        y = (sic.height - h) / 2
      }
      return {
        scale,
        x,
        y,
        width: w,
        height: h,
      }
    },
    sourceImgShadeStyle() {
      let { sourceImgMasking, sourceImgContainer } = this,
        sic = sourceImgContainer,
        sim = sourceImgMasking,
        w = sim.width == sic.width ? sim.width : (sic.width - sim.width) / 2,
        h =
          sim.height == sic.height ? sim.height : (sic.height - sim.height) / 2
      return {
        width: w + 'px',
        height: h + 'px',
      }
    },
    previewStyle() {
      let { width, height, ratio, previewContainer } = this,
        pc = previewContainer,
        w = pc.width,
        h = pc.height,
        pcRatio = w / h
      if (ratio < pcRatio) {
        w = pc.height * ratio
      }
      if (ratio > pcRatio) {
        h = pc.width / ratio
      }
      return {
        width: w + 'px',
        height: h + 'px',
      }
    },
  },
  watch: {
    value(newValue) {
      if (newValue && this.loading != 1) {
        this.reset()
      }
    },
  },
  created() {
    document.addEventListener('keyup', this.handleEscClose)
  },
  beforeDestroy() {
    document.removeEventListener('keyup', this.handleEscClose)
  },
  mounted() {
    if (this.sourceImgUrl) {
      this.startCrop()
    }
  },
  methods: {
    ripple(e) {
      effectRipple(e)
    },
    off() {
      setTimeout(() => {
        this.$emit('input', false)
        if (this.step == 3 && this.loading == 2) {
          this.setStep(1)
        }
      }, 200)
    },
    setStep(no) {
      setTimeout(() => {
        this.step = no
      }, 200)
    },
    preventDefault(e) {
      e.preventDefault()
      return false
    },
    handleClick(e) {
      if (this.loading !== 1) {
        if (e.target !== this.$refs.fileinput) {
          e.preventDefault()
          if (document.activeElement !== this.$refs) {
            this.$refs.fileinput.click()
          }
        }
      }
    },
    handleChange(e) {
      e.preventDefault()
      if (this.loading !== 1) {
        let files = e.target.files || e.dataTransfer.files
        this.reset()
        if (this.checkFile(files[0])) {
          this.setSourceImg(files[0])
        }
      }
    },
    checkFile(file) {
      let that = this,
        { lang, maxSize } = that
      if (file.type.indexOf('image') === -1) {
        that.hasError = true
        that.errorMsg = lang.error.onlyImg
        return false
      }
      if (file.size / 1024 > maxSize) {
        that.hasError = true
        that.errorMsg = lang.error.outOfSize + maxSize + 'kb'
        return false
      }
      return true
    },
    reset() {
      let that = this
      that.loading = 0
      that.hasError = false
      that.errorMsg = ''
      that.progress = 0
    },
    setSourceImg(file) {
      this.$emit('src-file-set', file.name, file.type, file.size)
      let that = this,
        fr = new FileReader()
      fr.onload = function (e) {
        that.sourceImgUrl = fr.result
        that.startCrop()
      }
      fr.readAsDataURL(file)
    },
    startCrop() {
      let that = this,
        { width, height, ratio, scale, sourceImgUrl, sourceImgMasking, lang } =
          that,
        sim = sourceImgMasking,
        img = new Image()
      img.src = sourceImgUrl
      img.onload = function () {
        let nWidth = img.naturalWidth,
          nHeight = img.naturalHeight,
          nRatio = nWidth / nHeight,
          w = sim.width,
          h = sim.height,
          x = 0,
          y = 0
        if (nWidth < width || nHeight < height) {
          that.hasError = true
          that.errorMsg = lang.error.lowestPx + width + '*' + height
          return false
        }
        if (ratio > nRatio) {
          h = w / nRatio
          y = (sim.height - h) / 2
        }
        if (ratio < nRatio) {
          w = h * nRatio
          x = (sim.width - w) / 2
        }
        scale.range = 0
        scale.x = x
        scale.y = y
        scale.width = w
        scale.height = h
        scale.minWidth = w
        scale.minHeight = h
        scale.maxWidth = nWidth * sim.scale
        scale.maxHeight = nHeight * sim.scale
        scale.naturalWidth = nWidth
        scale.naturalHeight = nHeight
        that.sourceImg = img
        that.createImg()
        that.setStep(2)
      }
    },
    imgStartMove(e) {
      e.preventDefault()
      if (this.isSupportTouch && !e.targetTouches) {
        return false
      }
      let et = e.targetTouches ? e.targetTouches[0] : e,
        { sourceImgMouseDown, scale } = this,
        simd = sourceImgMouseDown
      simd.mX = et.screenX
      simd.mY = et.screenY
      simd.x = scale.x
      simd.y = scale.y
      simd.on = true
    },
    imgMove(e) {
      e.preventDefault()
      if (this.isSupportTouch && !e.targetTouches) {
        return false
      }
      let et = e.targetTouches ? e.targetTouches[0] : e,
        {
          sourceImgMouseDown: { on, mX, mY, x, y },
          scale,
          sourceImgMasking,
        } = this,
        sim = sourceImgMasking,
        nX = et.screenX,
        nY = et.screenY,
        dX = nX - mX,
        dY = nY - mY,
        rX = x + dX,
        rY = y + dY
      if (!on) return
      if (rX > 0) {
        rX = 0
      }
      if (rY > 0) {
        rY = 0
      }
      if (rX < sim.width - scale.width) {
        rX = sim.width - scale.width
      }
      if (rY < sim.height - scale.height) {
        rY = sim.height - scale.height
      }
      scale.x = rX
      scale.y = rY
    },
    handleMouseWheel(e) {
      e = e || window.event
      let { scale } = this
      if (e.wheelDelta) {
        if (e.wheelDelta > 0) {
          this.zoomImg(scale.range >= 100 ? 100 : ++scale.range)
        }
        if (e.wheelDelta < 0) {
          this.zoomImg(scale.range <= 0 ? 0 : --scale.range)
        }
      } else if (e.detail) {
        if (e.detail > 0) {
          this.zoomImg(scale.range >= 100 ? 100 : ++scale.range)
        }
        if (e.detail < 0) {
          this.zoomImg(scale.range <= 0 ? 0 : --scale.range)
        }
      }
    },
    startZoomAdd(e) {
      let that = this,
        { scale } = that
      scale.zoomAddOn = true

      function zoom() {
        if (scale.zoomAddOn) {
          let range = scale.range >= 100 ? 100 : ++scale.range
          that.zoomImg(range)
          setTimeout(function () {
            zoom()
          }, 60)
        }
      }
      zoom()
    },

    endZoomAdd(e) {
      this.scale.zoomAddOn = false
    },

    startZoomSub(e) {
      let that = this,
        { scale } = that
      scale.zoomSubOn = true

      function zoom() {
        if (scale.zoomSubOn) {
          let range = scale.range <= 0 ? 0 : --scale.range
          that.zoomImg(range)
          setTimeout(function () {
            zoom()
          }, 60)
        }
      }
      zoom()
    },

    endZoomSub(e) {
      let { scale } = this
      scale.zoomSubOn = false
    },
    zoomChange(e) {
      this.zoomImg(e.target.value)
    },

    zoomImg(newRange) {
      let that = this,
        { sourceImgMasking, sourceImgMouseDown, scale } = this,
        {
          maxWidth,
          maxHeight,
          minWidth,
          minHeight,
          width,
          height,
          x,
          y,
          range,
        } = scale,
        sim = sourceImgMasking,
        sWidth = sim.width,
        sHeight = sim.height,
        nWidth = minWidth + ((maxWidth - minWidth) * newRange) / 100,
        nHeight = minHeight + ((maxHeight - minHeight) * newRange) / 100,
        nX = sWidth / 2 - (nWidth / width) * (sWidth / 2 - x),
        nY = sHeight / 2 - (nHeight / height) * (sHeight / 2 - y)

      if (nX > 0) {
        nX = 0
      }
      if (nY > 0) {
        nY = 0
      }
      if (nX < sWidth - nWidth) {
        nX = sWidth - nWidth
      }
      if (nY < sHeight - nHeight) {
        nY = sHeight - nHeight
      }

      scale.x = nX
      scale.y = nY
      scale.width = nWidth
      scale.height = nHeight
      scale.range = newRange
      setTimeout(function () {
        if (scale.range == newRange) {
          that.createImg()
        }
      }, 300)
    },

    createImg(e) {
      let that = this,
        {
          imgFormat,
          imgBgc,
          mime,
          sourceImg,
          scale: { x, y, width, height },
          sourceImgMasking: { scale },
        } = that,
        canvas = that.$refs.canvas,
        ctx = canvas.getContext('2d')
      if (e) {
        that.sourceImgMouseDown.on = false
      }
      canvas.width = that.width
      canvas.height = that.height
      ctx.clearRect(0, 0, that.width, that.height)

      if (imgFormat == 'png') {
        ctx.fillStyle = 'rgba(0,0,0,0)'
      } else {
        ctx.fillStyle = imgBgc
      }
      ctx.fillRect(0, 0, that.width, that.height)

      ctx.drawImage(
        sourceImg,
        x / scale,
        y / scale,
        width / scale,
        height / scale
      )
      that.createImgUrl = canvas.toDataURL(mime)
    },
    prepareUpload() {
      let { url, createImgUrl, field, ki } = this
      this.$emit('crop-success', createImgUrl, field, ki)
      if (typeof url == 'string' && url) {
        this.upload()
      } else {
        this.off()
      }
    },

    upload() {
      let that = this,
        {
          lang,
          imgFormat,
          mime,
          url,
          params,
          headers,
          field,
          ki,
          createImgUrl,
          withCredentials,
          method,
        } = this,
        fmData = new FormData()

      if (typeof params == 'object' && params) {
        Object.keys(params).forEach((k) => {
          fmData.append(k, params[k])
        })
      }

      fmData.append(
        field,
        data2blob(createImgUrl, mime),
        field + '.' + imgFormat
      )

      const uploadProgress = function (event) {
        if (event.lengthComputable) {
          that.progress = (100 * Math.round(event.loaded)) / event.total
        }
      }

      that.reset()
      that.loading = 1
      that.setStep(3)
      new Promise(function (resolve, reject) {
        let client = new XMLHttpRequest()
        client.open(method, url, true)
        client.withCredentials = withCredentials
        client.onreadystatechange = function () {
          if (this.readyState !== 4) {
            return
          }
          if (
            this.status === 200 ||
            this.status === 201 ||
            this.staus === 202
          ) {
            resolve(JSON.parse(this.responseText))
          } else {
            reject(this.status)
          }
        }
        client.upload.addEventListener('progress', uploadProgress, false) //监听进度

        if (typeof headers == 'object' && headers) {
          Object.keys(headers).forEach((k) => {
            client.setRequestHeader(k, headers[k])
          })
        }
        client.send(fmData)
      }).then(
        function (resData) {
          if (that.value) {
            that.loading = 2
            that.$emit('crop-upload-success', resData, field, ki)
          }
        },

        function (sts) {
          if (that.value) {
            that.loading = 3
            that.hasError = true
            that.errorMsg = lang.fail
            that.$emit('crop-upload-fail', sts, field, ki)
          }
        }
      )
    },
  },
  handleEscClose(e) {
    if (this.value && (e.key == 'Escape' || e.keyCode == 27)) {
      this.off()
    }
  },
}
</script>

<style lang="scss">
@charset "UTF-8";
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

body.dark-layout {
  .vue-image-crop-upload .vicp-wrap {
    background-color: $theme-dark-body-bg;
    border: 1px solid #fff;
  }
  .vue-image-crop-upload .vicp-wrap .vicp-step1 .vicp-drop-area,
  .vicp-description-area {
    background-color: $theme-dark-card-bg;
    color: #fff;
  }
  .vue-image-crop-upload .vicp-wrap .vicp-step1 .vicp-drop-area .vicp-hint {
    color: #fff;
  }

  .vue-image-crop-upload .vicp-range .vicp-icon5,
  .vue-image-crop-upload .vicp-range .vicp-icon6 {
    background-color: $theme-dark-card-bg;
  }

  .vue-image-crop-upload .vicp-range .vicp-icon5:hover,
  .vue-image-crop-upload .vicp-range .vicp-icon6:hover {
    background-color: $theme-dark-switch-bg;
  }

  .vue-image-crop-upload
    .vicp-range
    input[type='range']::-webkit-slider-runnable-track {
    background-color: $theme-dark-card-bg;
  }
  .vue-image-crop-upload .vicp-range input[type='range']::-moz-range-track {
    background-color: $theme-dark-card-bg;
  }
  .vue-image-crop-upload .vicp-range input[type='range']::-ms-fill-lower {
    background-color: $theme-dark-card-bg;
  }
  .vue-image-crop-upload .vicp-range input[type='range']::-ms-fill-upper {
    background-color: $theme-dark-card-bg;
  }
  .vue-image-crop-upload
    .vicp-range
    input[type='range']:focus::-webkit-slider-runnable-track {
    background-color: $theme-dark-card-bg;
  }
  .vue-image-crop-upload
    .vicp-range
    input[type='range']:focus::-moz-range-track {
    background-color: $theme-dark-card-bg;
  }
  .vue-image-crop-upload .vicp-range input[type='range']:focus::-ms-fill-lower {
    background-color: $theme-dark-card-bg;
  }
  .vue-image-crop-upload .vicp-range input[type='range']:focus::-ms-fill-upper {
    background-color: $theme-dark-card-bg;
  }
}

@-webkit-keyframes vicp_progress {
  0% {
    background-position-y: 0;
  }
  100% {
    background-position-y: 40px;
  }
}
@keyframes vicp_progress {
  0% {
    background-position-y: 0;
  }
  100% {
    background-position-y: 40px;
  }
}

@-webkit-keyframes vicp {
  0% {
    opacity: 0;
    -webkit-transform: scale(0) translatey(-60px);
    transform: scale(0) translatey(-60px);
  }
  100% {
    opacity: 1;
    -webkit-transform: scale(1) translatey(0);
    transform: scale(1) translatey(0);
  }
}

@keyframes vicp {
  0% {
    opacity: 0;
    -webkit-transform: scale(0) translatey(-60px);
    transform: scale(0) translatey(-60px);
  }
  100% {
    opacity: 1;
    -webkit-transform: scale(1) translatey(0);
    transform: scale(1) translatey(0);
  }
}

.vue-image-crop-upload {
  position: fixed;
  display: block;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  z-index: 10000;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.65);
  -webkit-tap-highlight-color: transparent;
  -moz-tap-highlight-color: transparent;
}
.vue-image-crop-upload .vicp-wrap {
  -webkit-box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.23);
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.23);
  position: fixed;
  display: block;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  z-index: 10000;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
  width: 100%;
  height: 100%;
  max-width: 760px;
  max-height: 420px;
  padding: 25px;
  background-color: #fff;
  border-radius: $border-radius;
  -webkit-animation: vicp 0.12s ease-in;
  animation: vicp 0.12s ease-in;
}

.vicp-step2 {
  padding-top: 8px;
}

.vue-image-crop-upload .vicp-wrap .vicp-close {
  position: absolute;
  right: -30px;
  top: -30px;
}
.vue-image-crop-upload .vicp-wrap .vicp-close .vicp-icon4 {
  position: relative;
  display: block;
  width: 30px;
  height: 30px;
  cursor: pointer;
  -webkit-transition: -webkit-transform 0.18s;
  transition: -webkit-transform 0.18s;
  transition: transform 0.18s;
  transition: transform 0.18s, -webkit-transform 0.18s;
  -webkit-transform: rotate(0);
  -ms-transform: rotate(0);
  transform: rotate(0);
}
.vue-image-crop-upload .vicp-wrap .vicp-close .vicp-icon4::after,
.vue-image-crop-upload .vicp-wrap .vicp-close .vicp-icon4::before {
  -webkit-box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.23);
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.23);
  content: '';
  position: absolute;
  top: 12px;
  left: 4px;
  width: 22px;
  height: 5px;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
  background-color: #fff;
}
.vue-image-crop-upload .vicp-wrap .vicp-close .vicp-icon4::after {
  -webkit-transform: rotate(-45deg);
  -ms-transform: rotate(-45deg);
  transform: rotate(-45deg);
}
.vue-image-crop-upload .vicp-wrap .vicp-close .vicp-icon4:hover {
  -webkit-transform: rotate(90deg);
  -ms-transform: rotate(90deg);
  transform: rotate(90deg);
}
.vue-image-crop-upload .vicp-wrap .vicp-step1 .vicp-drop-area {
  position: relative;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 260px;
  background-color: rgba(0, 0, 0, 0.03);
  text-align: center;
  border: 1px dashed rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.vicp-description {
  height: 304px;
}

.vicp-description-area {
  height: 180px;
  padding: 30px 24px;
  text-align: justify;
  background-color: rgba(0, 0, 0, 0.03);
}

.vue-image-crop-upload .vicp-wrap .vicp-step1 .vicp-drop-area .vicp-icon1 {
  display: block;
  margin: 0 auto 6px;
  width: 42px;
  height: 42px;
  overflow: hidden;
}
.vue-image-crop-upload
  .vicp-wrap
  .vicp-step1
  .vicp-drop-area
  .vicp-icon1
  .vicp-icon1-arrow {
  display: block;
  margin: 0 auto;
  width: 0;
  height: 0;
  border-bottom: 14.7px solid rgba(0, 0, 0, 0.3);
  border-left: 14.7px solid transparent;
  border-right: 14.7px solid transparent;
}
.vue-image-crop-upload
  .vicp-wrap
  .vicp-step1
  .vicp-drop-area
  .vicp-icon1
  .vicp-icon1-body {
  display: block;
  width: 12.6px;
  height: 14.7px;
  margin: 0 auto;
  background-color: rgba(0, 0, 0, 0.3);
}
.vue-image-crop-upload
  .vicp-wrap
  .vicp-step1
  .vicp-drop-area
  .vicp-icon1
  .vicp-icon1-bottom {
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  display: block;
  height: 12.6px;
  border: 6px solid rgba(0, 0, 0, 0.3);
  border-top: none;
}
.vue-image-crop-upload .vicp-wrap .vicp-step1 .vicp-drop-area .vicp-hint {
  display: block;
  padding: 15px;
  font-size: 14px;
  color: #666;
  line-height: 30px;
}
.vue-image-crop-upload
  .vicp-wrap
  .vicp-step1
  .vicp-drop-area
  .vicp-no-supported-hint {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  padding: 30px;
  width: 100%;
  height: 60px;
  line-height: 30px;
  background-color: #eee;
  text-align: center;
  color: #666;
  font-size: 14px;
}
.vue-image-crop-upload .vicp-wrap .vicp-step1 .vicp-drop-area:hover {
  cursor: pointer;
  border-color: rgba(0, 0, 0, 0.1);
  background-color: rgba(0, 0, 0, 0.05);
}
.vue-image-crop-upload .vicp-img-container {
  position: relative;
  display: block;
  width: 396px;
  height: 304px;
  background-color: #e5e5e0;
  overflow: hidden;
}
.vue-image-crop-upload .vicp-img-container .vicp-img {
  position: absolute;
  display: block;
  cursor: move;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.vue-image-crop-upload .vicp-img-container .vicp-img-shade {
  -webkit-box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.18);
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.18);
  position: absolute;
  background-color: rgba(241, 242, 243, 0.8);
}
.vue-image-crop-upload .vicp-img-container .vicp-img-shade.vicp-img-shade-1 {
  top: 0;
  left: 0;
}
.vue-image-crop-upload .vicp-img-container .vicp-img-shade.vicp-img-shade-2 {
  bottom: 0;
  right: 0;
}

$track-background: #e2e2e2;

.vue-image-crop-upload .vicp-range {
  position: relative;
  width: 100%;
  height: 30px;
}
.vue-image-crop-upload .vicp-range .vicp-icon5,
.vue-image-crop-upload .vicp-range .vicp-icon6 {
  position: absolute;
  top: 0;
  width: 30px;
  height: 30px;
  border-radius: 100%;
  background-color: $track-background;
}
.vue-image-crop-upload .vicp-range .vicp-icon5:hover,
.vue-image-crop-upload .vicp-range .vicp-icon6:hover {
  -webkit-box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.12);
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.12);
  cursor: pointer;
  background-color: #f8f8f8;
}
.vue-image-crop-upload .vicp-range .vicp-icon5 {
  left: 0;
}
.vue-image-crop-upload .vicp-range .vicp-icon5::before {
  position: absolute;
  content: '';
  display: block;
  left: 9px;
  top: 14px;
  width: 12px;
  height: 2px;
  background-color: #fff;
}
.vue-image-crop-upload .vicp-range .vicp-icon6 {
  right: 0;
}
.vue-image-crop-upload .vicp-range .vicp-icon6::before {
  position: absolute;
  content: '';
  display: block;
  left: 9px;
  top: 14px;
  width: 12px;
  height: 2px;
  background-color: #fff;
}
.vue-image-crop-upload .vicp-range .vicp-icon6::after {
  position: absolute;
  content: '';
  display: block;
  top: 9px;
  left: 14px;
  width: 2px;
  height: 12px;
  background-color: #fff;
}

.vue-image-crop-upload .vicp-range input[type='range'] {
  display: block;
  margin: 0 auto;
  width: calc(100% - 82px);
  height: 30px;
  background: transparent;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  cursor: pointer;
}
.vue-image-crop-upload .vicp-range input[type='range']:focus {
  outline: none;
}
.vue-image-crop-upload .vicp-range input[type='range']::-webkit-slider-thumb {
  -webkit-box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.18);
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.18);
  -webkit-appearance: none;
  appearance: none;
  margin-top: -3px;
  width: 30px;
  height: 30px;
  background-color: $primary;
  border-radius: 100%;
  border: none;
  -webkit-transition: 0.2s;
  transition: 0.2s;
}
.vue-image-crop-upload .vicp-range input[type='range']::-moz-range-thumb {
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.18);
  -moz-appearance: none;
  appearance: none;
  width: 30px;
  height: 30px;
  background-color: $primary;
  border-radius: 100%;
  border: none;
  -webkit-transition: 0.2s;
  transition: 0.2s;
}
.vue-image-crop-upload .vicp-range input[type='range']::-ms-thumb {
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.18);
  appearance: none;
  width: 30px;
  height: 30px;
  background-color: $primary;
  border: none;
  border-radius: 100%;
  -webkit-transition: 0.2s;
  transition: 0.2s;
}
.vue-image-crop-upload
  .vicp-range
  input[type='range']:active::-moz-range-thumb {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.23);
  width: 30px;
  height: 30px;
}
.vue-image-crop-upload .vicp-range input[type='range']:active::-ms-thumb {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.23);
  width: 30px;
  height: 30px;
}
.vue-image-crop-upload
  .vicp-range
  input[type='range']:active::-webkit-slider-thumb {
  -webkit-box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.23);
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.23);
  margin-top: -4px;
  width: 30px;
  height: 30px;
}
.vue-image-crop-upload
  .vicp-range
  input[type='range']::-webkit-slider-runnable-track {
  -webkit-box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.12);
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.12);
  width: 100%;
  height: 24px;
  cursor: pointer;
  border-radius: 20px;
  border: none;
  background-color: $track-background;
}
.vue-image-crop-upload .vicp-range input[type='range']::-moz-range-track {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.12);
  width: 100%;
  height: 24px;
  cursor: pointer;
  border-radius: 20px;
  border: none;
  background-color: $track-background;
}
.vue-image-crop-upload .vicp-range input[type='range']::-ms-track {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.12);
  width: 100%;
  cursor: pointer;
  background: transparent;
  border-color: transparent;
  color: transparent;
  height: 24px;
  border-radius: 20px;
  border: none;
}
.vue-image-crop-upload .vicp-range input[type='range']::-ms-fill-lower {
  background-color: $track-background;
}
.vue-image-crop-upload .vicp-range input[type='range']::-ms-fill-upper {
  background-color: $track-background;
}
.vue-image-crop-upload
  .vicp-range
  input[type='range']:focus::-webkit-slider-runnable-track {
  background-color: $track-background;
}
.vue-image-crop-upload .vicp-range input[type='range']:focus::-moz-range-track {
  background-color: $track-background;
}
.vue-image-crop-upload .vicp-range input[type='range']:focus::-ms-fill-lower {
  background-color: $track-background;
}
.vue-image-crop-upload .vicp-range input[type='range']:focus::-ms-fill-upper {
  background-color: $track-background;
}
.vue-image-crop-upload .vicp-wrap .vicp-step3 .vicp-upload {
  position: relative;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  padding: 35px;
  height: 260px;
  background-color: rgba(0, 0, 0, 0.03);
  text-align: center;
  border: 1px dashed #ddd;
}

.vue-image-crop-upload .vicp-wrap .vicp-step3 .vicp-upload .vicp-progress-wrap {
  margin-top: 12px;
  background-color: rgba(0, 0, 0, 0.08);
  border-radius: 3px;
}
.vue-image-crop-upload
  .vicp-wrap
  .vicp-step3
  .vicp-upload
  .vicp-progress-wrap
  .vicp-progress {
  position: relative;
  display: block;
  height: 5px;
  border-radius: 3px;
  background-color: #4a7;
  -webkit-box-shadow: 0 2px 6px 0 rgba(68, 170, 119, 0.3);
  box-shadow: 0 2px 6px 0 rgba(68, 170, 119, 0.3);
  -webkit-transition: width 0.15s linear;
  transition: width 0.15s linear;
  background-image: -webkit-linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.2) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.2) 75%,
    transparent 75%,
    transparent
  );
  background-image: linear-gradient(
    -45deg,
    rgba(255, 255, 255, 0.2) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.2) 75%,
    transparent 75%,
    transparent
  );
  background-size: 40px 40px;
  -webkit-animation: vicp_progress 0.5s linear infinite;
  animation: vicp_progress 0.5s linear infinite;
}
.vue-image-crop-upload
  .vicp-wrap
  .vicp-step3
  .vicp-upload
  .vicp-progress-wrap
  .vicp-progress::after {
  content: '';
  position: absolute;
  display: block;
  top: -3px;
  right: -3px;
  width: 9px;
  height: 9px;
  border: 1px solid rgba(245, 246, 247, 0.7);
  -webkit-box-shadow: 0 1px 4px 0 rgba(68, 170, 119, 0.7);
  box-shadow: 0 1px 4px 0 rgba(68, 170, 119, 0.7);
  border-radius: 100%;
  background-color: #4a7;
}
.vue-image-crop-upload .vicp-wrap .vicp-operate {
  position: absolute;
  right: 20px;
  bottom: 20px;
}
.vue-image-crop-upload .vicp-wrap .vicp-operate a {
  position: relative;
  float: left;
  display: block;
  margin-left: 10px;
  width: 100px;
  height: 36px;
  line-height: 36px;
  text-align: center;
  cursor: pointer;
  font-size: 14px;
  color: #4a7;
  border-radius: 2px;
  overflow: hidden;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.vue-image-crop-upload .vicp-wrap .vicp-operate a:hover {
  background-color: rgba(0, 0, 0, 0.03);
}
.vue-image-crop-upload .vicp-wrap .vicp-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #d10;
}
.vue-image-crop-upload .vicp-wrap .vicp-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #4a7;
}
</style>
